if (!customElements.get('essential-collection-tabs')) {
  class EssentialCollectionTabs extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-tab-target]'));
      this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.activate(button.dataset.tabTarget));
      });
    }

    activate(id) {
      this.buttons.forEach((button) => {
        const isActive = button.dataset.tabTarget === id;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive);
      });
      this.panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.tabPanel === id);
      });
    }
  }

  customElements.define('essential-collection-tabs', EssentialCollectionTabs);
}

if (!customElements.get('essential-email-popup-launcher')) {
  class EssentialEmailPopupLauncher extends HTMLElement {
    connectedCallback() {
      const modal = document.getElementById('EssentialEmailPopup');
      if (!modal) return;
      const storageKey = this.dataset.storageKey || 'essential-email-popup';
      const delay = Number(this.dataset.delay || 4000);

      if ((window.Shopify && Shopify.designMode) || localStorage.getItem(storageKey) === 'closed') return;

      const markClosed = () => localStorage.setItem(storageKey, 'closed');
      const closeButton = modal.querySelector('[id^="ModalClose-"]');
      if (closeButton) closeButton.addEventListener('click', markClosed);
      modal.addEventListener('click', (event) => {
        if (event.target === modal) markClosed();
      });
      modal.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') markClosed();
      });

      window.setTimeout(() => {
        if (!modal.hasAttribute('open')) modal.show(this);
      }, delay);
    }
  }

  customElements.define('essential-email-popup-launcher', EssentialEmailPopupLauncher);
}

if (!customElements.get('essential-quick-view')) {
  class EssentialQuickView extends HTMLElement {
    connectedCallback() {
      this.modal = document.getElementById('EssentialQuickViewModal');
      this.content = this.modal ? this.modal.querySelector('[data-quick-view-content]') : null;
      this.currency = this.dataset.currency || 'CLP';
      this.locale = this.dataset.locale || 'es-CL';
      this.moneyFormatter = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: this.currency === 'CLP' ? 0 : 2,
      });

      if (!this.modal || !this.content) return;

      if (this.dataset.quickViewBound === 'true') return;
      this.dataset.quickViewBound = 'true';

      const stopCardNavigation = (event) => {
        const button = event.target.closest('[data-quick-view-handle]');
        if (!button) return null;
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
        return button;
      };

      document.addEventListener(
        'pointerdown',
        (event) => {
          stopCardNavigation(event);
        },
        true
      );

      document.addEventListener(
        'click',
        (event) => {
          const button = stopCardNavigation(event);
          if (!button) return;
          this.open(button);
        },
        true
      );
    }

    open(button) {
      const handle = button.dataset.quickViewHandle;
      if (!handle || !this.modal || !this.content) return;
      this.content.innerHTML = '<div class="essential-quick-view__loading">Cargando producto...</div>';
      this.modal.show(button);
      fetch(`/products/${handle}.js`)
        .then((response) => {
          if (!response.ok) throw new Error('quick-view-fetch');
          return response.json();
        })
        .then((product) => {
          this.product = product;
          this.render(product);
        })
        .catch(() => {
          this.content.innerHTML =
            '<div class="essential-quick-view__error">No fue posible cargar la vista rápida.</div>';
        });
    }

    render(product) {
      const featured = product.featured_image || (product.images && product.images[0]) || '';
      const hasVariants = product.variants.length > 1;
      const options = (product.options || []).map((option, index) =>
        typeof option === 'string' ? { name: option, position: index + 1, values: [] } : option
      );

      const optionGroups = options
        .map((option, index) => {
          const isColor = /color/i.test(option.name);
          const values =
            option.values && option.values.length
              ? option.values
              : [...new Set(product.variants.map((variant) => variant.options[index]))];

          const items = values
            .map((value) => {
              const variant = product.variants.find((v) => v.options[index] === value);
              const image = variant && variant.featured_image ? variant.featured_image.src : '';
              const label = this.escape(value);
              if (isColor && image) {
                return `<button type="button" class="essential-quick-view__swatch" data-option-index="${index}" data-option-value="${label}" title="${label}" aria-label="${label}">
                    <img src="${this.sizedUrl(image, 200)}" alt="" width="100" height="100" loading="lazy">
                  </button>`;
              }
              return `<button type="button" class="essential-quick-view__variant" data-option-index="${index}" data-option-value="${label}">${label}</button>`;
            })
            .join('');

          return `<div class="essential-quick-view__variants">
              <span class="essential-quick-view__option-label">${this.escape(option.name)}: <strong data-option-selected="${index}"></strong></span>
              <div class="essential-quick-view__variant-options${isColor ? ' essential-quick-view__variant-options--swatches' : ''}">${items}</div>
            </div>`;
        })
        .join('');

      const plainDescription = product.description
        ? this.escape(product.description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()).substring(0, 320)
        : '';

      this.content.innerHTML = `
        <div class="essential-quick-view__inner">
          <div class="essential-quick-view__media">
            ${featured ? `<img src="${this.sizedUrl(featured, 900)}" alt="${this.escape(product.title)}" data-quick-view-image>` : ''}
          </div>
          <div class="essential-quick-view__info">
            <div>
              <h2 class="essential-quick-view__title h2">${this.escape(product.title)}</h2>
            </div>
            <div class="essential-quick-view__price" data-price-wrapper></div>
            ${hasVariants ? optionGroups : ''}
            ${
              plainDescription
                ? `<div class="essential-quick-view__description">${plainDescription}...</div>`
                : ''
            }
            <div class="essential-quick-view__actions">
              <button type="button" class="button" data-add-to-cart>Agregar al carrito</button>
              <a class="button button--secondary" href="${product.url}">Ver producto</a>
            </div>
          </div>
        </div>
      `;

      this.optionButtons = Array.from(this.content.querySelectorAll('[data-option-index]'));
      this.mainImage = this.content.querySelector('[data-quick-view-image]');
      this.addToCartButton = this.content.querySelector('[data-add-to-cart]');
      const initialVariant = product.variants.find((variant) => variant.available) || product.variants[0];
      this.setVariant(initialVariant ? initialVariant.id : null);

      this.optionButtons.forEach((button) => {
        button.addEventListener('click', () =>
          this.selectOption(Number(button.dataset.optionIndex), button.dataset.optionValue)
        );
      });

      if (this.addToCartButton) {
        this.addToCartButton.addEventListener('click', () => this.addToCart());
      }
    }

    selectOption(index, value) {
      const selected = [...this.activeVariant.options];
      selected[index] = value;
      const variant =
        this.product.variants.find((v) => v.options.every((option, i) => option === selected[i])) ||
        this.product.variants.find((v) => v.options[index] === value && v.available) ||
        this.product.variants.find((v) => v.options[index] === value);
      if (variant) this.setVariant(variant.id);
    }

    setVariant(variantId) {
      this.activeVariant = this.product.variants.find((variant) => variant.id === Number(variantId)) || this.product.variants[0];
      this.optionButtons.forEach((button) => {
        const index = Number(button.dataset.optionIndex);
        button.classList.toggle('is-active', this.activeVariant.options[index] === button.dataset.optionValue);
      });
      this.content.querySelectorAll('[data-option-selected]').forEach((label) => {
        label.textContent = this.activeVariant.options[Number(label.dataset.optionSelected)] || '';
      });

      if (this.mainImage && this.activeVariant.featured_image) {
        this.mainImage.src = this.sizedUrl(this.activeVariant.featured_image.src, 900);
      }

      const priceWrapper = this.content.querySelector('[data-price-wrapper]');
      const compareAt = this.activeVariant.compare_at_price;
      priceWrapper.innerHTML = `
        ${this.moneyFormatter.format(this.activeVariant.price / 100)}
        ${compareAt && compareAt > this.activeVariant.price ? `<span class="essential-quick-view__compare">${this.moneyFormatter.format(compareAt / 100)}</span>` : ''}
      `;

      if (this.addToCartButton) {
        this.addToCartButton.disabled = !this.activeVariant.available;
        this.addToCartButton.textContent = this.activeVariant.available ? 'Agregar al carrito' : 'Agotado';
      }
    }

    addToCart() {
      if (!this.activeVariant || !this.activeVariant.available) return;
      this.addToCartButton.disabled = true;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', this.activeVariant.id);
      formData.append('quantity', 1);

      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender()
            .map((section) => section.id)
            .join(',')
        );
        formData.append('sections_url', window.location.pathname);
      }

      fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      })
        .then((response) => {
          if (!response.ok) throw new Error('quick-view-cart');
          return response.json();
        })
        .then((response) => {
          this.addToCartButton.textContent = 'Agregado';
          if (cart && response.sections) cart.renderContents(response);
        })
        .catch(() => {
          this.addToCartButton.disabled = false;
          this.addToCartButton.textContent = 'Intentar otra vez';
        });
    }

    sizedUrl(src, width) {
      if (!src) return '';
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', width);
        return url.href;
      } catch (error) {
        return src;
      }
    }

    escape(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  customElements.define('essential-quick-view', EssentialQuickView);
}
